class BundleProductCard {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.sectionContainer = document.querySelector(`#BundleSection-${sectionId}`);
    if (!this.sectionContainer) return;
    
    this.discountPercentage = parseInt(this.sectionContainer.dataset.discountPercentage) || 0;
    this.requiredWrappers = this.sectionContainer.querySelectorAll('.bundle-card-wrapper--required');
    this.optionalWrappers = this.sectionContainer.querySelectorAll('.bundle-list-item--optional');
    this.addToCartBtn = this.sectionContainer.querySelector('[data-bundle-add-to-cart]');
    
    // UI Elements for updates
    this.originalTotalEls = this.sectionContainer.querySelectorAll('.original-total');
    this.discountedTotalEls = this.sectionContainer.querySelectorAll('.discounted-total');
    this.itemCountEl = this.sectionContainer.querySelector('.bundle-item-count');
    this.savingsBadgeEl = this.sectionContainer.querySelector('.bundle-savings-badge');
    
    this.init();
  }
  
  init() {
    this.updateBundleUI();
    this.bindEvents();
  }

  bindEvents() {
    // Optional product selection
    this.optionalWrappers.forEach(wrapper => {
      wrapper.addEventListener('click', (e) => {
        // Don't toggle selection if clicking a color swatch
        if (e.target.closest('[data-bundle-color-swatches]')) return;

        const isSelected = wrapper.getAttribute('aria-selected') === 'true';
        wrapper.setAttribute('aria-selected', !isSelected);
        this.updateBundleUI();
      });
    });

    // Variant Dropdown change
    const variantSelects = this.sectionContainer.querySelectorAll('.bundle-variant-select');
    variantSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        this.handleVariantSwitch(select);
      });
      // Prevent parent click when clicking the select itself (for optional items)
      select.addEventListener('click', (e) => e.stopPropagation());
    });

    // Accordion toggle (if not handled by Liquid script)
    const trigger = this.sectionContainer.querySelector('[data-accordion-trigger]');
    const content = this.sectionContainer.querySelector('[data-accordion-content]');
    if (trigger && content) {
      trigger.addEventListener('click', () => {
        const isCurrentlyExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const newState = !isCurrentlyExpanded;
        
        trigger.setAttribute('aria-expanded', newState);
        
        if (newState) {
          content.classList.remove('hidden');
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.opacity = '1';
        } else {
          content.style.maxHeight = '0';
          content.style.opacity = '0';
          // Wait for transition to end before adding hidden if needed, 
          // but we use max-height 0 in CSS .hidden anyway
          setTimeout(() => content.classList.add('hidden'), 300);
        }
      });
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  formatMoney(cents) {
    return window.themeVariables.currencySymbol + (cents / 100).toFixed(2);
  }

  handleVariantSwitch(element) {
    const wrapper = element.closest('.bundle-card-wrapper, .bundle-list-item--optional');
    if (!wrapper) return;

    let variantId, variantPrice, variantImage, variantTitle;

    if (element.tagName === 'SELECT') {
      const selectedOption = element.options[element.selectedIndex];
      variantId = selectedOption.value;
      variantPrice = parseInt(selectedOption.dataset.variantPrice);
      variantImage = selectedOption.dataset.variantImage;
      variantTitle = selectedOption.dataset.variantTitle;
    } else {
      // Fallback for legacy buttons if any remain
      variantId = element.dataset.variantId;
      variantPrice = parseInt(element.dataset.variantPrice);
      variantImage = element.dataset.variantImage;
      variantTitle = element.dataset.variantTitle;
    }

    // Update wrapper data
    wrapper.dataset.defaultVariantId = variantId;
    wrapper.dataset.defaultVariantPrice = variantPrice;

    // Update Image
    const img = wrapper.querySelector('.bundle-item-image');
    if (img && variantImage) {
      img.src = variantImage;
    }

    // Update Variant Title
    const titleEl = wrapper.querySelector('.bundle-item-variant-title');
    if (titleEl) {
      titleEl.textContent = `| ${variantTitle}`;
    }

    // Update Price display (for required items mostly)
    const priceEl = wrapper.querySelector('.bundle-item-price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(variantPrice);
    }

    this.updateBundleUI();
  }

  updateBundleUI() {
    let subtotalCents = 0;
    let selectedCount = 0;

    // 1. Calculate base subtotal from required products
    this.requiredWrappers.forEach(wrapper => {
      const data = this.getCardData(wrapper);
      if (data) {
        subtotalCents += data.price;
        selectedCount++;
      }
    });

    // 2. Add selected optional products and update their individual prices
    this.optionalWrappers.forEach(wrapper => {
      const isSelected = wrapper.getAttribute('aria-selected') === 'true';
      const data = this.getCardData(wrapper);
      if (data) {
        // Update individual item's discounted price display
        const discountedPrice = Math.round(data.price * (1 - this.discountPercentage / 100));
        const priceEl = wrapper.querySelector('.discounted-price');
        if (priceEl) priceEl.textContent = this.formatMoney(discountedPrice);
        if (isSelected) {
          subtotalCents += data.price;
          selectedCount++;
        }
      }
    });

    // 3. Calculate bundle-wide discount
    const discountedTotalCents = Math.round(subtotalCents * (1 - this.discountPercentage / 100));
    const savingsCents = subtotalCents - discountedTotalCents;

    // 4. Update UI Elements
    this.originalTotalEls.forEach(el => el.textContent = this.formatMoney(subtotalCents));
    this.discountedTotalEls.forEach(el => el.textContent = this.formatMoney(discountedTotalCents));
    
    if (this.itemCountEl) {
      const itemsText = this.addToCartBtn?.querySelector('[data-items]')?.getAttribute('data-items');
      this.itemCountEl.textContent = `${selectedCount}${itemsText}`;
    }

    if (this.savingsBadgeEl) {
      this.savingsBadgeEl.textContent = `${this.formatMoney(savingsCents)}`;
      this.savingsBadgeEl.classList.toggle('hidden', savingsCents <= 0);
    }
  }
  
  getCardData(wrapper) {
    if (!wrapper) return null;
    
    // Check for default variant data attributes first
    if (wrapper.dataset.defaultVariantId) {
      return {
        variantId: wrapper.dataset.defaultVariantId,
        price: parseInt(wrapper.dataset.defaultVariantPrice) || 0
      };
    }
    
    const productCard = wrapper.querySelector('product-card');
    if (productCard) {
      const variantDropdown = productCard.querySelector('[data-bundle-variant-dropdown]');
      if (variantDropdown && variantDropdown.selectedIndex >= 0) {
        const selectedOption = variantDropdown.options[variantDropdown.selectedIndex];
        return {
          variantId: selectedOption.value,
          price: parseInt(selectedOption.getAttribute('data-variant-price')) || 0
        };
      }
    }
    
    return null;
  }
  
  async handleAddToCart() {
    const items = [];
    
    // Add required products
    this.requiredWrappers.forEach(wrapper => {
      const data = this.getCardData(wrapper);
      if (data && data.variantId) {
        items.push({ id: data.variantId, quantity: 1 });
      }
    });
    
    // Add selected optional products
    this.optionalWrappers.forEach(wrapper => {
      if (wrapper.getAttribute('aria-selected') === 'true') {
        const data = this.getCardData(wrapper);
        if (data && data.variantId) {
          items.push({ id: data.variantId, quantity: 1 });
        }
      }
    });

    if (items.length === 0) return;

    this.addToCartBtn.setAttribute('aria-busy', 'true');
    if (this.addToCartBtn.querySelector('.loader-button__loader')) {
       this.addToCartBtn.querySelector('.loader-button__loader').removeAttribute('hidden');
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        const text = await response.text();
        console.error('Failed to parse JSON response: teemo', text);
        responseData = { message: `Invalid response from server (HTTP ${response.status})` };
      }
      if (!response.ok) {
        throw new Error(responseData.message || responseData.description || 'Failed to add items to cart');
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      
    } catch (e) {
      alert(e.message || 'Error adding to cart. Please try again.');
    } finally {
      this.addToCartBtn.setAttribute('aria-busy', 'false');
      if (this.addToCartBtn.querySelector('.loader-button__loader')) {
        this.addToCartBtn.querySelector('.loader-button__loader').setAttribute('hidden', '');
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const initBundles = () => {
    document.querySelectorAll('[id^="BundleSection-"]').forEach(section => {
      if (!section.dataset.bundleInitialized) {
        const sectionId = section.id.replace('BundleSection-', '');
        new BundleProductCard(sectionId);
        section.dataset.bundleInitialized = 'true';
      }
    });
  };

  initBundles();
  document.addEventListener('shopify:section:load', initBundles);
});